import { type ReactNode, useEffect, useState } from "react";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";

import { initMsal, msalInstance, signIn } from "@/lib/auth/msal";

/**
 * App-level auth provider.
 *
 * On mount, it awaits MSAL's initialise + redirect handshake. Until
 * that's done, children get `null` so we don't render gated UI before
 * accounts are known.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    initMsal()
      .catch((err) => {
        // Not throwing - we still want the app to render so the user
        // can re-attempt sign-in. Log to console for diagnosis.
        // eslint-disable-next-line no-console
        console.error("MSAL initialise failed", err);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return null;
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}

/**
 * Route guard. Renders `children` when authenticated; otherwise shows
 * a sign-in prompt. Used by the orchestration views (any route that
 * needs to call the API).
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const isAuthed = useIsAuthenticated();
  const { inProgress } = useMsal();

  if (inProgress !== "none") {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <span className="font-mono text-sm">
          ⟩ Completing sign-in&hellip;
        </span>
      </div>
    );
  }

  if (!isAuthed) {
    return (
      <div className="mx-auto max-w-md py-20 text-center">
        <h2 className="text-2xl font-semibold">Sign in to continue</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          PTRN runs against your Azure subscription. Sign in with the
          same Microsoft account you use for Azure.
        </p>
        <button
          type="button"
          onClick={() => {
            void signIn();
          }}
          className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
        >
          ⟩ Sign in with Microsoft
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
